import { useState, useMemo } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  ColumnDef,
  ExpandedState,
  Row,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { CaretRightIcon, CaretDownIcon } from '@phosphor-icons/react';

export interface TreeTableProps<TData extends { children?: TData[] }> {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  getRowId?: (row: TData) => string;
  className?: string;
  maxHeight?: number | string;
  emptyMessage?: string;
  defaultExpanded?: boolean | 'all' | 'first';
  onRowClick?: (row: TData, depth: number) => void;
  indentSize?: number;
  stickyHeader?: boolean;
  rowClassName?: (row: TData, depth: number) => string;
}

/**
 * TreeTable - A hierarchical table component using TanStack Table
 *
 * Supports n-level nested data via the `children` property on each row.
 * Automatically handles expand/collapse state and indentation.
 */
export function TreeTable<TData extends { children?: TData[] }>({
  data,
  columns,
  getRowId,
  className,
  maxHeight = 600,
  emptyMessage = 'No data available.',
  defaultExpanded = false,
  onRowClick,
  indentSize = 24,
  stickyHeader = true,
  rowClassName,
}: TreeTableProps<TData>) {
  // Calculate initial expanded state
  const initialExpanded = useMemo((): ExpandedState => {
    if (defaultExpanded === 'all') {
      return true; // TanStack Table understands `true` as "expand all"
    }
    if (defaultExpanded === 'first') {
      // Expand only the first row at each level
      const expanded: Record<string, boolean> = {};
      const expandFirst = (rows: TData[], parentPath = '') => {
        if (rows.length > 0) {
          const firstRow = rows[0];
          const rowId = getRowId
            ? getRowId(firstRow)
            : parentPath
              ? `${parentPath}.0`
              : '0';
          expanded[rowId] = true;
          if (firstRow.children && firstRow.children.length > 0) {
            expandFirst(firstRow.children, rowId);
          }
        }
      };
      expandFirst(data);
      return expanded;
    }
    if (defaultExpanded === true) {
      return true;
    }
    return {};
  }, [defaultExpanded, data, getRowId]);

  const [expanded, setExpanded] = useState<ExpandedState>(initialExpanded);

  const table = useReactTable({
    data,
    columns,
    state: {
      expanded,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.children,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowId: getRowId,
  });

  const allColumns = table.getAllColumns();

  return (
    <div
      className={cn('rounded-lg border overflow-auto', className)}
      style={{ maxHeight }}
    >
      <Table className="relative">
        <TableHeader className={cn(stickyHeader && 'sticky top-0 z-10')}>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              className="bg-gray-200 text-foreground"
            >
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className="font-semibold text-xs h-10 p-2"
                  style={{ width: header.getSize() }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={allColumns.length}
                className="h-24 text-center text-muted-foreground"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            table
              .getRowModel()
              .rows.map((row) => (
                <TreeTableRow
                  key={row.id}
                  row={row}
                  isExpanded={row.getIsExpanded()}
                  indentSize={indentSize}
                  onRowClick={onRowClick}
                  rowClassName={rowClassName}
                />
              ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

interface TreeTableRowProps<TData extends { children?: TData[] }> {
  row: Row<TData>;
  isExpanded: boolean;
  indentSize: number;
  onRowClick?: (row: TData, depth: number) => void;
  rowClassName?: (row: TData, depth: number) => string;
}

function TreeTableRow<TData extends { children?: TData[] }>({
  row,
  isExpanded,
  indentSize,
  onRowClick,
  rowClassName,
}: TreeTableRowProps<TData>) {
  const depth = row.depth;
  const canExpand = row.getCanExpand();
  // First cell gets the expand button and indentation
  const cells = row.getVisibleCells();

  const customClassName = rowClassName ? rowClassName(row.original, depth) : '';

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    row.toggleExpanded();
  };

  return (
    <TableRow
      className={cn(
        'border-b border-border',
        depth === 0 && 'font-semibold',
        depth === 1 && 'bg-gray-50',
        depth >= 2 && 'bg-gray-100/50',
        onRowClick && 'cursor-pointer hover:bg-accent/50',
        customClassName,
      )}
      onClick={() => onRowClick?.(row.original, depth)}
    >
      {cells.map((cell, cellIndex) => {
        const isFirstCell = cellIndex === 0;

        return (
          <TableCell
            key={cell.id}
            className={cn('text-xs p-2', isFirstCell && 'relative')}
          >
            {isFirstCell ? (
              <div
                className="flex items-center gap-1"
                style={{ paddingLeft: depth * indentSize }}
              >
                {canExpand ? (
                  <button
                    type="button"
                    onClick={handleToggleExpand}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="flex-shrink-0 p-1 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-1 focus:ring-gray-400"
                    aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                  >
                    {isExpanded ? (
                      <CaretDownIcon className="h-4 w-4" />
                    ) : (
                      <CaretRightIcon className="h-4 w-4" />
                    )}
                  </button>
                ) : (
                  <span className="w-6 flex-shrink-0" /> // Spacer for alignment
                )}
                <span className="truncate">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </span>
              </div>
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
}

export type { ColumnDef } from '@tanstack/react-table';
